import React, { forwardRef, useEffect, useImperativeHandle } from "react";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldType } from "../interfaces/FieldType";
import { FormValues } from "../interfaces/FormData";
import PaginatedSelect from "./PaginatedSelect";
import { useTheme } from "../../context/ThemeContext";
import { tryConvertToBase64 } from "../utils/toBase64File";

export interface FieldConfig {
  key: keyof FormValues;
  label: string;
  type: FieldType;
  options?: { value: any; label: string }[];
  placeholder?: string;
  inputType?: string;
  validations?: {
    required?: boolean;
    pattern?: RegExp;
    minLength?: number;
    maxLength?: number;
  };
  showWhen?: (formValues: FormValues) => boolean;
  defaultValue?: any;
  size?: number;
  notOnForm?: boolean;
  hideField?: boolean;
  isMultiple?: boolean;
  onSelect?: any;
  fetchOptions?: (
    page: number,
    search: string
  ) => Promise<{ options: any[]; totalPages: number }>;
  /**
   * If provided, the final data object will store the field’s value under this name
   * instead of `key`. Example: patchFrom: "registration_status".
   */
  patchFrom?: {
    objectKey: string;
    valueKey: string;
    labelKey: string;
  };
}

export interface DynamicFormProps {
  fields: FieldConfig[];
  onSubmit?: (data: FormValues) => void;
  onChange?: (data: FormValues) => void;
  onCancel?: () => void;
  title?: string;
  defaultValues?: Partial<FormValues>;
  showSubmit?: boolean;
  showCancel?: boolean;
  submitText?: string;
  cancelText?: string;
}

export interface DynamicFormHandle {
  submitForm: () => void;
}

// tiny helper to check required
const isRequired = (field: FieldConfig) =>
  field?.validations?.required === true;

const DynamicForm = forwardRef<DynamicFormHandle, DynamicFormProps>(
  (
    {
      fields,
      onSubmit,
      onChange,
      onCancel,
      title,
      defaultValues = {},
      showSubmit = true,
      showCancel = true,
      submitText = "submit",
      cancelText = "cancel",
    },
    ref
  ) => {
    const validationSchema = Yup.object(
      fields.reduce((schema, field) => {
        let validator:
          | Yup.StringSchema<string | undefined, Yup.AnyObject>
          | Yup.ArraySchema<string[] | undefined, Yup.AnyObject, "", "">;

        if (field.isMultiple) {
          validator = Yup.array()
            .of(Yup.string().required(`${field.label} is required`))
            .min(1, `${field.label} must have at least one selected option`);
        } else {
          validator = Yup.string();

          if (field.validations?.required) {
            validator = validator.required(`${field.label} is required`);
          }
          if (field.validations?.minLength) {
            validator = validator.min(
              field.validations.minLength,
              `${field.label} must be at least ${field.validations.minLength} characters`
            );
          }
          if (field.validations?.maxLength) {
            validator = validator.max(
              field.validations.maxLength,
              `${field.label} must be at most ${field.validations.maxLength} characters`
            );
          }
          if (field.validations?.pattern) {
            validator = validator.matches(
              field.validations.pattern,
              `${field.label} is invalid`
            );
          }
        }

        schema[field.key] = validator;
        return schema;
      }, {} as Record<string, Yup.AnySchema>)
    );

    const {
      control,
      handleSubmit,
      watch,
      formState: { errors },
      reset,
    } = useForm<FormValues>({
      resolver: yupResolver(validationSchema),
      defaultValues: defaultValues || {},
    });

    useEffect(() => {
      const processedDefaults = { ...defaultValues };

      fields.forEach((field) => {
        if (field.type === FieldType.CHECKBOX) {
          processedDefaults[field.key] = Boolean(defaultValues?.[field.key]);
        }
      });

      reset(processedDefaults);
    }, [defaultValues, fields, reset]);

    const { theme } = useTheme();

    const formValues = watch();
    useEffect(() => {
      if (onChange) {
        const subscription = watch((value) => {
          onChange(value);
        });
        return () => subscription.unsubscribe();
      }
    }, [watch, onChange]);
    const textColors =
      theme === "night"
        ? "text-gray-500 border-gray-900"
        : "text-primary-700 border-primary-500";

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit((formData) => {
          const convertedData = Object.keys(formData).reduce((acc, key) => {
            const fieldConfig = fields.find((f) => f.key === key);

            if (fieldConfig?.type === FieldType.CHECKBOX) {
              // Ensure checkbox values are converted to boolean
              acc[key] = !!formData[key];
            } else if (fieldConfig?.inputType === "number") {
              acc[key] =
                formData[key] !== "" && formData[key] !== null
                  ? parseFloat(formData[key] as any)
                  : null;
            } else if (fieldConfig?.inputType === "date") {
              const dateValue = formData[key] as any;
              if (dateValue) {
                const isoDate = new Date(
                  `${dateValue}T00:00:00Z`
                ).toISOString();
                acc[key] = isoDate;
              } else {
                acc[key] = null;
              }
            }
            return acc;
          }, {} as { [key: string]: any });

          onSubmit?.(convertedData);
        })();
      },
    }));

    return (
      <div
        className={`w-full rounded-3xl p-4 max-h-[800px] min-w-[1200px] overflow-x-auto 
                ${
                  theme === "night"
                    ? "bg-gray-700 text-gray-400"
                    : "bg-primary-100 text-primary-800"
                }`}
      >
        {title && <h2 className="text-xl font-bold mb-4">{title}</h2>}
        <form
          onSubmit={handleSubmit((data) => onSubmit?.(data))}
          className="grid grid-cols-12 gap-4"
        >
          {fields.map((field) => {
            if (field.showWhen && !field.showWhen(formValues)) {
              return null;
            }

            return (
              <div
                key={String(field.key)}
                className={`col-span-12 md:col-span-${field.size || 12} ${
                  field.hideField ? "hidden" : ""
                }`}
              >
                <label
                  htmlFor={String(field.key)}
                  className={`block text-sm font-medium mb-1 ${
                    theme === "night" ? "text-gray-400" : "text-primary-800"
                  }`}
                  aria-required={isRequired(field) ? "true" : "false"}
                >
                  {field.label}
                  {isRequired(field) && (
                    <span className="text-red-600 ml-0.5">*</span>
                  )}
                </label>
                <Controller
                  name={String(field.key)}
                  control={control}
                  render={({ field: controllerField }) => {
                    switch (field.type) {
                      case FieldType.INPUT:
                        if (field.hideField) {
                          return (
                            <input
                              type="hidden"
                              {...controllerField}
                              value={controllerField.value ?? ""}
                            />
                          );
                        }
                        return (
                          <input
                            id={String(field.key)}
                            type={field.inputType || "text"}
                            placeholder={field.placeholder}
                            value={controllerField.value ?? ""}
                            onChange={(e) => {
                              const value =
                                field.inputType === "number"
                                  ? e.target.value === ""
                                    ? ""
                                    : parseFloat(e.target.value)
                                  : e.target.value;
                              controllerField.onChange(value);
                            }}
                            className={`form-control border-2 rounded-2xl p-2 w-full ${textColors}`}
                            required={isRequired(field)}
                            aria-required={isRequired(field)}
                          />
                        );
                      case FieldType.TEXTAREA:
                        return (
                          <textarea
                            id={String(field.key)}
                            {...controllerField}
                            placeholder={field.placeholder}
                            className={`form-control border-2 rounded-2xl p-2 w-full ${textColors}`}
                            required={isRequired(field)}
                            aria-required={isRequired(field)}
                          />
                        );
                      case FieldType.SELECT:
                        return (
                          <select
                            id={String(field.key)}
                            value={controllerField.value ?? ""}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const value =
                                field.inputType === "number"
                                  ? raw === ""
                                    ? ""
                                    : parseFloat(raw)
                                  : raw;
                              controllerField.onChange(value);
                            }}
                            className={`form-control border-2 rounded-2xl p-2 w-full ${textColors}`}
                            required={isRequired(field)}
                            aria-required={isRequired(field)}
                          >
                            <option value="">Select an option</option>
                            {field.options?.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        );
                      case FieldType.PAGINATED_SELECT:
                        return (
                          <PaginatedSelect
                            fetchOptions={field.fetchOptions!}
                            placeholder={field.placeholder}
                            isMultiple={field.isMultiple}
                            defaultValue={defaultValues[field.key]}
                            defaultLabel={defaultValues[`${field.key}_label`]}
                            onSelect={(value: string | string[]) => {
                              let finalValue: any = value;
                              if (field.inputType === "number") {
                                if (Array.isArray(value)) {
                                  finalValue = value.map((v) =>
                                    v === "" ? "" : parseFloat(v)
                                  );
                                } else {
                                  finalValue =
                                    value === "" ? "" : parseFloat(value);
                                }
                              }
                              controllerField.onChange(finalValue);
                            }}
                            aria-required={isRequired(field)}
                          />
                        );
                      case FieldType.CHECKBOX:
                        return (
                          <div className="flex items-center">
                            <input
                              id={String(field.key)}
                              type="checkbox"
                              checked={!!controllerField.value}
                              onChange={(e) =>
                                controllerField.onChange(e.target.checked)
                              }
                              className={`form-checkbox ${textColors}`}
                              required={isRequired(field)}
                              aria-required={isRequired(field)}
                            />
                            <label
                              htmlFor={String(field.key)}
                              className="ml-2 text-sm"
                            >
                              {field.label}
                              {isRequired(field) && (
                                <span className="text-red-600 ml-0.5">*</span>
                              )}
                            </label>
                          </div>
                        );
                      case FieldType.RADIO:
                        return (
                          <div className="space-y-2">
                            {field.options?.map((option) => (
                              <label
                                key={option.value}
                                className="inline-flex items-center space-x-2"
                              >
                                <input
                                  type="radio"
                                  value={option.value}
                                  checked={
                                    controllerField.value === option.value
                                  }
                                  onChange={(e) =>
                                    controllerField.onChange(e.target.value)
                                  }
                                  className={`form-radio ${textColors}`}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        );
                      case FieldType.FILE:
                        return (
                          <input
                            type="file"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file instanceof File) {
                                const base64 = await tryConvertToBase64(file);
                                controllerField.onChange(base64);
                              } else {
                                controllerField.onChange(null);
                              }
                            }}
                            className={`form-control border-2 rounded-2xl p-2 w-full ${textColors}`}
                            required={isRequired(field)}
                            aria-required={isRequired(field)}
                          />
                        );
                      default:
                        return <div>Unsupported field type</div>;
                    }
                  }}
                />
                {errors[field.key] && (
                  <span className="text-danger-500 text-sm">
                    {errors[field.key]?.message as string}
                  </span>
                )}
              </div>
            );
          })}

          <div className="col-span-12 flex justify-between mt-4">
            {showCancel && showSubmit ? (
              <>
                <button
                  type="button"
                  className="border-danger-500 border-2 text-white px-4 py-2 rounded-2xl hover:bg-danger-200"
                  onClick={onCancel}
                >
                  {cancelText}
                </button>
                <button
                  type="submit"
                  className="border-tertiary-500 border-2 text-white px-4 py-2 rounded-2xl hover:bg-tertiary-200"
                >
                  {submitText}
                </button>
              </>
            ) : showCancel ? (
              <button
                type="button"
                className="border-danger-500 border-2 text-white px-4 py-2 rounded-2xl hover:bg-danger-200 ml-auto"
                onClick={onCancel}
              >
                {cancelText}
              </button>
            ) : showSubmit ? (
              <button
                type="submit"
                className="border-tertiary-500 border-2 text-white px-4 py-2 rounded-2xl hover:bg-tertiary-200 ml-auto"
              >
                {submitText}
              </button>
            ) : null}
          </div>
        </form>
      </div>
    );
  }
);

DynamicForm.displayName = "DynamicForm";
export default DynamicForm;
